import { metricsBus } from "@/common/buses";
import { useDeviceType } from "@/common/hooks";
import { useGetSearches } from "@/common/hooks/useGetSearches";
import { apiSearch, apiCreators } from "@/common/services";
import { ICreatorsItems, INichesItems, ITagsList, IUserSearchResults } from "@/common/services/types";
import { deviceTypeEnum } from "@/common/utils/device";
import { useCallback, useEffect, useMemo, useState } from "react";

interface IUseSearchResults {
  searchValue: string;
}

const MAX_DESKTOP_ITEMS = 5;
const MAX_MOBILE_ITEMS = 3;
const DELAY = 500;

export const useSearchResults = ({ searchValue }: IUseSearchResults) => {
  const [debouncedValue, setDebouncedValue] = useState<string>("");
  const searchText = useMemo(() => encodeURI(searchValue), [searchValue]);
  const deviceType = useDeviceType();
  const isPhone = useMemo(() => deviceType === deviceTypeEnum.PHONE, [deviceType]);

  const { isLoading: creatorsLoading, data: creators } = useGetSearches<IUserSearchResults>(
    "creators-searches",
    debouncedValue,
    useCallback((query: string) => apiCreators.searchCreators({ query: decodeURI(query), order: "best_match" }), [])
  );

  const { data: niches, isLoading: nichesLoading } = useGetSearches<INichesItems[]>(
    "niches-searches",
    debouncedValue,
    useCallback((query: string) => apiSearch.getNichesSearches(query), [])
  );

  const { data: tags, isLoading: tagsLoading } = useGetSearches<ITagsList[]>(
    "tags-searches",
    debouncedValue,
    useCallback((query: string) => apiSearch.getTagsSearches(query), []),
  );

  const hasSearchData = useMemo(() => niches && creators && tags, [niches, creators, tags]);

  const searchResults = useMemo(() => {
    const itemsLimit = isPhone ? MAX_MOBILE_ITEMS : MAX_DESKTOP_ITEMS;

    return {
      creators: (creators?.items?.slice(0, itemsLimit) ?? []).map(creator => {
        return {
          type: "creator",
          text: creator.username,
          image: creator.profileImageUrl,
          verified: creator.verified,
          name: creator.name ?? creator.username,
          userId: creator.username,
          studio: creator.studio ?? false,
          followers: creator.followers,
        } as ICreatorsItems;
      }),
      niches: niches?.slice(0, itemsLimit) ?? [],
      tags: tags?.slice(0, itemsLimit) ?? [],
    };
  }, [creators, niches, tags, isPhone]);

  const totalCount = useMemo(() => {
    return (creators?.items?.length || 0) + (niches?.length || 0) + (tags?.length || 0);
  }, [creators, niches, tags]);

  const sendSearchGaEvent = useCallback((query: string, totalCount: number) => {
    metricsBus.emit("searched", { query, totalCount });
  }, []);

  useEffect(() => {
    const delayInputTimeoutId = setTimeout(() => {
      setDebouncedValue(searchText);
    }, DELAY);
    return () => clearTimeout(delayInputTimeoutId);
  }, [searchText]);

  useEffect(() => {
    if (hasSearchData) {
      sendSearchGaEvent(searchText, totalCount);
    }
  }, [hasSearchData]);

  return {
    ...searchResults,
    creatorsLoading: creatorsLoading || creators === undefined,
    nichesLoading: nichesLoading || niches === undefined,
    tagsLoading: tagsLoading || tags === undefined,
  };
};
